import { useState } from "react";
import {
  SimpleGrid, Flex, Text, Icon, Spinner, useColorModeValue,
} from "@chakra-ui/react";
import { SubmissionFormModal } from "./SubmissionFormModal";
import { ErrorState } from "../../../components/ErrorState";
import { getFormIcon } from "./formPresentation";
import { useProcessHubFormsData } from "../forms/ProcessHubFormsContext";
import type { FormType } from "./types";

export function LeaderFormsGrid() {
  const { forms: allForms, loading, error, refresh } = useProcessHubFormsData();
  const forms = allForms.filter((f) => f.is_active);

  const titleColor = useColorModeValue("#434347", "#e8edf2");
  const cardBg = useColorModeValue("white", "#0f2040");
  const cardBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const iconChipBg = useColorModeValue("rgba(0, 158, 251, 0.1)", "rgba(0, 158, 251, 0.16)");
  const descColor = useColorModeValue("#696a71", "#8fa3b8");
  const cardShadowHover = useColorModeValue(
    "0 1px 2px rgba(0,0,0,0.04), 0 16px 36px -12px rgba(0,0,0,0.18)",
    "0 1px 2px rgba(0,0,0,0.4), 0 16px 36px -12px rgba(0,0,0,0.8)"
  );

  const [activeForm, setActiveForm] = useState<FormType | null>(null);

  const handleOpen = (form: FormType) => setActiveForm(form);

  const closeModal = () => setActiveForm(null);

  if (loading) {
    return (
      <Flex justify="center" align="center" py={16}>
        <Spinner size="md" color="#009efb" />
      </Flex>
    );
  }

  if (error) {
    return <ErrorState onRetry={refresh} />;
  }

  if (forms.length === 0) {
    return <Text color={descColor} fontSize="sm">Nenhum formulário disponível no momento.</Text>;
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5}>
        {forms.map((form) => {
          const FormIcon = getFormIcon(form.name);
          return (
            <Flex
              key={form.id}
              bg={cardBg}
              border="1px solid"
              borderColor={cardBorder}
              borderRadius="16px"
              boxShadow="0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.12)"
              p={6}
              align="center"
              gap={4}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ boxShadow: cardShadowHover, transform: "translateY(-2px)" }}
              onClick={() => handleOpen(form)}
            >
              <Flex align="center" justify="center" w="52px" h="52px" borderRadius="14px" bg={iconChipBg} flexShrink={0}>
                <Icon as={FormIcon} boxSize={6} color="#009efb" />
              </Flex>
              <Text fontSize="md" fontWeight={600} color={titleColor}>{form.name}</Text>
            </Flex>
          );
        })}
      </SimpleGrid>

      <SubmissionFormModal
        isOpen={activeForm !== null}
        onClose={closeModal}
        formType={activeForm}
      />
    </>
  );
}